import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { repositoryAPI, contributorAPI } from '../../services/api';
import { repoKeys } from '../../query/queryKeys';
import { normalizeContributorRows } from '../../utils/contributorChartRows';

function buildCommitsParams(commitsListFilters) {
  const f = commitsListFilters;
  return {
    limit: 50,
    ...(f.branch?.trim() ? { branch: f.branch.trim() } : {}),
    ...(f.author?.trim() ? { author: f.author.trim() } : {}),
    ...(f.from?.trim() ? { from: f.from.trim() } : {}),
    ...(f.to?.trim() ? { to: f.to.trim() } : {}),
  };
}

const BRANCHES_OPTS = { limit: 500 };

/**
 * Tab-specific repository data (commits, activity, PRs, contributors, branches, DORA metrics).
 * Queries are enabled only when their tab (or related tab) is active.
 */
export function useRepositoryTabData({
  id,
  orgId,
  activeTab,
  commitsListFilters,
  contributorsTimeframe,
  contributorsStatus,
  deploymentWindowDays,
  leadTimeWindowDays,
  churnWindowDays,
  historyBranch,
}: any) {
  const metricsTabActive = activeTab === 'overview' || activeTab === 'metrics';
  const commitsParams = useMemo(() => buildCommitsParams(commitsListFilters), [commitsListFilters]);

  const commitsQuery = useQuery({
    queryKey: repoKeys.commits(id, commitsParams),
    queryFn: async () => {
      const res = await repositoryAPI.getCommits(id, commitsParams);
      return { commits: res?.commits ?? [], meta: res?.meta ?? null };
    },
    enabled: !!id && activeTab === 'commits',
  });

  const commitFilterBranchesQuery = useQuery({
    queryKey: repoKeys.branches(id, BRANCHES_OPTS),
    queryFn: async () => {
      const res = await repositoryAPI.getBranches(id, BRANCHES_OPTS);
      return Array.isArray(res.branches) ? res.branches : [];
    },
    enabled: !!id && activeTab === 'commits',
  });

  const historyQuery = useQuery({
    queryKey: repoKeys.commits(id, { branch: historyBranch, limit: 60, __view: 'history' }),
    queryFn: async () => {
      const res = await repositoryAPI.getCommits(id, { branch: historyBranch, limit: 60 });
      return res?.commits ?? [];
    },
    enabled: !!id && !!historyBranch && activeTab === 'history',
  });

  const activityQuery = useQuery({
    queryKey: repoKeys.activity(id),
    queryFn: async () => {
      const data = await repositoryAPI.getActivity(id, { limit: 50 });
      return Array.isArray(data) ? data : [];
    },
    enabled: !!id && activeTab === 'activity',
  });

  const pullRequestsQuery = useQuery({
    queryKey: repoKeys.pullRequests(id),
    queryFn: async () => {
      const res = await repositoryAPI.getPullRequests(id, {});
      return {
        pullRequests: res?.pullRequests ?? [],
        stats: res?.stats ?? null,
      };
    },
    enabled: !!id && activeTab === 'pull-requests',
  });

  const contributorsQuery = useQuery({
    queryKey: repoKeys.contributors(id),
    queryFn: async () => {
      const data = await contributorAPI.getByRepository(id);
      return Array.isArray(data) ? data : [];
    },
    enabled: !!id && (activeTab === 'contributors' || activeTab === 'commits'),
  });

  const contributorsCardQuery = useQuery({
    queryKey: repoKeys.contributorsCard(id, contributorsTimeframe, contributorsStatus),
    queryFn: async () => {
      const data = await contributorAPI.getByRepository(id, {
        timeframe: contributorsTimeframe,
        status: contributorsStatus,
      });
      return Array.isArray(data) ? data : [];
    },
    enabled: !!id && activeTab === 'contributors',
  });

  const branchesQuery = useQuery({
    queryKey: repoKeys.branches(id, BRANCHES_OPTS),
    queryFn: async () => {
      const res = await repositoryAPI.getBranches(id, BRANCHES_OPTS);
      return {
        branches: Array.isArray(res.branches) ? res.branches : [],
        stats: res.stats ?? null,
      };
    },
    enabled: !!id && activeTab === 'branches',
  });

  const deploymentDoraQuery = useQuery({
    queryKey: repoKeys.dora(id, deploymentWindowDays),
    queryFn: async () => {
      const data = await repositoryAPI.getDora(id, deploymentWindowDays);
      return { dora: data?.dora ?? null, meta: data?.meta ?? null };
    },
    enabled: !!id && metricsTabActive,
  });

  const leadTimeDoraQuery = useQuery({
    queryKey: repoKeys.dora(id, leadTimeWindowDays),
    queryFn: async () => {
      const data = await repositoryAPI.getDora(id, leadTimeWindowDays);
      return { dora: data?.dora ?? null, meta: data?.meta ?? null };
    },
    enabled: !!id && metricsTabActive,
  });

  const churnQuery = useQuery({
    queryKey: repoKeys.churn(id, churnWindowDays),
    queryFn: () => repositoryAPI.getChurn(id, churnWindowDays),
    enabled: !!id && metricsTabActive,
  });

  const tabActivity = useMemo(() => activityQuery.data ?? [], [activityQuery.data]);
  const activityTypeOptions = useMemo(() => {
    const s = new Set();
    tabActivity.forEach((a) => {
      if (a?.type) s.add(String(a.type));
    });
    return [...s].sort((a, b) => String(a).localeCompare(String(b)));
  }, [tabActivity]);

  const contributorRows = useMemo(
    () => normalizeContributorRows(contributorsCardQuery.data ?? []),
    [contributorsCardQuery.data]
  );

  return {
    tabCommits: commitsQuery.data?.commits ?? [],
    tabCommitsMeta: commitsQuery.data?.meta ?? null,
    tabCommitsLoading: commitsQuery.isPending,
    commitFilterBranches: commitFilterBranchesQuery.data ?? [],
    tabActivity,
    tabActivityLoading: activityQuery.isPending,
    activityTypeOptions,
    pullRequests: pullRequestsQuery.data?.pullRequests ?? [],
    pullRequestsStats: pullRequestsQuery.data?.stats ?? null,
    pullRequestsLoading: pullRequestsQuery.isPending,
    contributors: contributorsQuery.data ?? [],
    contributorsLoading: contributorsQuery.isPending,
    contributorRows,
    contributorsCardLoading: contributorsCardQuery.isPending,
    tabBranches: branchesQuery.data?.branches ?? [],
    tabBranchesStats: branchesQuery.data?.stats ?? null,
    tabBranchesLoading: branchesQuery.isPending,
    deploymentDora: deploymentDoraQuery.data?.dora ?? null,
    deploymentDoraMeta: deploymentDoraQuery.data?.meta ?? null,
    deploymentLoading: deploymentDoraQuery.isPending,
    leadTimeDora: leadTimeDoraQuery.data?.dora ?? null,
    leadTimeDoraMeta: leadTimeDoraQuery.data?.meta ?? null,
    leadTimeLoading: leadTimeDoraQuery.isPending,
    churnData: churnQuery.data ?? null,
    churnLoading: churnQuery.isPending,
    historyCommits: historyQuery.data ?? [],
    historyLoading: historyQuery.isPending,
  };
}
